/**
 * Payment Methods - Unified constants and types
 * This file centralizes all payment method definitions used across the application
 */

export const PAYMENT_METHODS = [
  { value: 'DINHEIRO', label: 'Dinheiro' },
  { value: 'CARTAO_CREDITO', label: 'Cartão de Crédito' },
  { value: 'BOLETO_BANCARIO', label: 'Boleto Bancário' },
  { value: 'CARTAO_CREDITO_VIA_LINK', label: 'Cartão de Crédito via Link' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CARTAO_DEBITO', label: 'Cartão de Débito' },
  { value: 'TRANSFERENCIA_BANCARIA', label: 'Transferência Bancária' },
  { value: 'PIX', label: 'PIX' },
  { value: 'OUTRO', label: 'Outro' },
  { value: 'CARTEIRA_DIGITAL', label: 'Carteira Digital' },
  { value: 'CASHBACK', label: 'Cashback' },
] as const;

export type PaymentMethodValue = typeof PAYMENT_METHODS[number]['value'];

export interface PaymentMethod {
  value: PaymentMethodValue;
  label: string;
}

// Mapping from UPPER_CASE values to labels
const paymentMethodLabelByValue = Object.fromEntries(
  PAYMENT_METHODS.map((method) => [method.value, method.label])
) as Record<PaymentMethodValue, string>;

// Reverse mapping for database values (lowercase first word)
// boleto -> Boleto Bancário, cartao -> Cartão de Crédito, etc.
const lowercaseToLabel: Record<string, string> = {
  'boleto': 'Boleto Bancário',
  'cartao': 'Cartão de Crédito',
  'dinheiro': 'Dinheiro',
  'cheque': 'Cheque',
  'transferencia': 'Transferência Bancária',
  'pix': 'PIX',
  'outro': 'Outro',
  'carteira': 'Carteira Digital',
  'cashback': 'Cashback',
};

/**
 * Get the display label for a payment method value
 * Handles both UPPER_CASE (from forms) and lowercase (from database) formats
 */
export const getPaymentMethodLabel = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  // Try UPPER_CASE mapping first (for frontend usage)
  if (paymentMethodLabelByValue[value as PaymentMethodValue]) {
    return paymentMethodLabelByValue[value as PaymentMethodValue];
  }

  // Fallback to lowercase mapping (for database values)
  if (lowercaseToLabel[value]) {
    return lowercaseToLabel[value];
  }

  // If no mapping found, return the value as-is
  return value;
};
